import Container from '~/components/Container.tsx';
import Stack from '~/components/Stack.tsx';
import Button from '~/components/Button.tsx';
import ImageView from '~/components/ImageView.tsx';
import Headline from '~/components/Headline.tsx';
import ModalPopup from '~/components/ModalPopup.tsx';
import { BasketProductItemResponse } from '~kup/models/Basket.ts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Typography from '~/components/Typography.tsx';
import { memo, useState } from 'react';
import Divider from '~/components/Divider.tsx';
import ProductList from '~/components/ProductList.tsx';
import { extractProductEventData } from '~/utils/product.ts';
import useBatchLogger from '~/hooks/useBatchLogger.ts';
import useRelatedProducts from '~/hooks/useRelatedProducts.ts';
import KupShoppingBenefits from '~/components/KupShoppingBenefits.tsx';
export type RemoveItemsPopoverProps = {
  open?: boolean;
  onClose: () => void;
  addedItems: BasketProductItemResponse[];
};

const SHOW_LIMIT = 4;

export default memo(AddedToBasketPopup);

function AddedToBasketPopup(props: RemoveItemsPopoverProps) {
  const { open, onClose, addedItems } = props;
  const [showAll, setShowAll] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { logAction } = useBatchLogger();
  const { relatedProducts } = useRelatedProducts(addedItems[0]?.productId ?? '');

  const handleGoToBasket = () => {
    navigate('/shop/basket');
  };

  const handleClose = () => {
    onClose();
  };

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  return (
    <ModalPopup
      open={open}
      title={
        <Typography variant={'TitleM_B'}>
          {i18n.t('shop-product-addedToBasket', { count: addedItems.length })}
        </Typography>
      }
      onClose={onClose}
      bottomPosition={'sticky'}
      fixedBottom={
        <Container
          pb={'20px'}
          pt={'4px'}
          px={'20px'}
          background={'paper'}
          sx={() => ({ zIndex: 1 })}
          stackProps={{
            row: true,
            gap: '8px',
          }}
        >
          <Button fullWidth variant={'contained'} color={'primary'} onClick={handleGoToBasket}>
            {i18n.t('shop-product-goToBasket')}
          </Button>
          <Button fullWidth variant={'contained'} color={'fill'} onClick={handleClose}>
            {i18n.t('shop-product-keepShopping')}
          </Button>
        </Container>
      }
    >
      <Container px={'20px'} pt={'16px'}>
        <Stack sx={{ width: '100%' }} column gap={'16px'} alignItems={'center'}>
          {addedItems.slice(0, showAll ? addedItems.length : SHOW_LIMIT).map((item) => {
            const key = 'added' + item.productId + item.stock.id;
            return (
              <Stack row key={key} gap={'16px'} sx={{ width: '100%' }} alignItems={'center'}>
                <ImageView
                  borderRadius={'8px'}
                  width={'60px'}
                  height={'60px'}
                  src={item.productImageUrls[0]}
                />

                <Headline
                  subHeadline={item.productName}
                  headline={item.optionItemSelections.map((o) => o.name).join(',')}
                  description={i18n.t('shop-product-items', { count: item.quantity })}
                />
              </Stack>
            );
          })}
          {!showAll && SHOW_LIMIT < addedItems.length && (
            <Button
              fullWidth
              size={'small'}
              onClick={toggleShowAll}
              variant={'text'}
              color={'primary'}
            >
              {i18n.t('shop-product-showAll', { count: addedItems.length })}
            </Button>
          )}
        </Stack>
      </Container>
      <Container pt={'20px'} pb={'12px'}>
        <Divider type={'line'} />
      </Container>
      <KupShoppingBenefits />
      <Container py={'12px'}>
        <Divider type={'line'} />
      </Container>
      <Container background={'paper'}>
        <ProductList
          type={'simple'}
          title={
            i18n.t('shop-product-dontMissThis') // Don't miss this product!
          }
          products={relatedProducts}
          onClickProductItem={(product) => {
            logAction({
              eventType: 'CLICK',
              eventTarget: 'PRODUCT_RELATED_PRODUCT',
              eventCategory: 'product',
              eventData: extractProductEventData(product),
            });
          }}
          onLikeProductItem={(product) => {
            if (!product.isLiked) {
              logAction({
                eventType: 'CLICK',
                eventTarget: 'PRODUCT_LIKE',
                eventCategory: 'product',
                eventData: extractProductEventData(product, { type: 'related' }),
              });
            }
          }}
        />
      </Container>
    </ModalPopup>
  );
}
